import { useState } from 'react';
import { DataTable, StatusField, Icon, EmptyState, Pagination, useSpinner } from '@chikara/ui';
import { formatCurrency } from '@chikara/utils';
import dataNotFound from '@/features/card/assets/empty-list-icon.svg';

import { ACTIVITY_MOVEMENT_COLUMNS, ACTIVITY_MOVEMENT_MOBILE_CONFIG } from '@/features/card/utils/tableStatus';
import styles from '@/features/card/pages/CardDetailPage/CardDetailPage.module.css';
import { t } from '@/i18n';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ReportChargebackModal } from '@/features/dashboard/components/ReportChargebackModal/ReportChargebackModal';


interface CardDetailActivityProps {
    data: any[];
    pagination?: any;
}

const formatMXN = (value: number) => formatCurrency(value, { locale: 'es-MX', currency: 'MXN' });

export const CardDetailActivity = ({ data, pagination }: CardDetailActivityProps) => {
    const { isSpinnerLoading } = useSpinner();
    const { userData } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
    

    const basic = isBasic(userData.role);

    const renderers: Record<string, (value: any, row: any) => React.ReactNode> = {
        movement: (value, row) => {
            const amount = row.amount as number;
            const isNegative = amount < 0;
            const mov = value as any;
            return (
                <div className={styles.movementContainer}>
                <div className={isNegative ? styles.iconContainerOut : styles.iconContainerIn}>
                    <Icon
                    name={isNegative ? 'transaction-out' : 'transaction-in'}
                    size="2xl"
                    color="inherit"
                    />
                </div>

                <div className={styles.movementCell}>
                    <div className={styles.movementDescription}>{mov.description}</div>
                    <div className={styles.movementDetails}>
                    {mov.subcategory}
                    </div>
                </div>
                </div>
            );
        },
        status: (value, row) => (
            <StatusField
                status={row.statusName as string}
                statusType={value as any}
                actionLabel={row.report ? t('dashboard.card.detail.activity.reportAction') : undefined}
                showReportIcon={row.report}
                onActionClick={() => {
                    setSelectedTransaction(row);
                    setIsModalOpen(true);
                }}
                reportTooltip={row.report ? t('dashboard.reportTooltip') : undefined}
            />
        ),
        amount: (value) => {
            const amount = typeof value === 'number' ? value : parseFloat(value as string);
            const isNegative = amount < 0;
            const formattedAmount = formatMXN(Math.abs(amount));
            return (
                <span className={isNegative ? styles.negativeAmount : styles.positiveAmount}>
                    {isNegative ? `-${formattedAmount}` : formattedAmount}
                </span>
            );
        },
    };

    return (
        <div className={styles.activitySection}>
            <h2 className={styles.activityTitle}>{t('dashboard.card.detail.activity.title')}</h2>
            {(data.length === 0 && !isSpinnerLoading) ? (
                <EmptyState
                    title={t('dashboard.emptyState.title')}
                    description={basic ? t('dashboard.emptyState.descriptionBasicRole') : t('dashboard.emptyState.description')}
                    imgSrc={dataNotFound}
                    maxWidth='460px'
                />
            ) : (
                <>
                <DataTable
                    columns={ACTIVITY_MOVEMENT_COLUMNS}
                    data={data}
                    mobileConfig={ACTIVITY_MOVEMENT_MOBILE_CONFIG}
                    isLoading={isSpinnerLoading}
                    renderCell={(key, value, row) => {
                        const renderer = renderers[key];
                        return renderer ? renderer(value, row) : value;
                    }}
                />
                {pagination && <Pagination 
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={(page) => pagination.onPageChange(page, pagination.itemsPerPage)}
                    showInfo
                    infoText={t('dashboard.paginationInfo', {
                        start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                        end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                        total: pagination.totalItems
                    })}
                />}
                </>
            )}
            <ReportChargebackModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                transaction={selectedTransaction}
            />
        </div>
    );
};
